import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, name, password);
      toast.success("Account created. Welcome to Noteology.");
      navigate("/app/home", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // Emergent's managed OAuth only allows redirects to *.emergent.host / *.emergentagent.com.
    // For custom domains, we route through the OAuth bridge page on the .emergent.host URL,
    // which then forwards the session_id back to the custom domain.
    const origin = window.location.origin;
    const isWhitelistedOrigin =
      /\.emergent\.host$/i.test(origin) ||
      /\.emergentagent\.com$/i.test(origin) ||
      /^http:\/\/localhost(:\d+)?$/i.test(origin);

    let redirectUrl;
    if (isWhitelistedOrigin) {
      redirectUrl = origin + "/app/home";
    } else {
      const bridgeHost = process.env.REACT_APP_EMERGENT_HOST || "https://ai-notetaker-3.emergent.host";
      const returnTo = encodeURIComponent(origin + "/app/home");
      redirectUrl = `${bridgeHost}/auth/oauth-bridge?return_to=${returnTo}`;
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-background">
      <div className="relative hidden md:flex flex-col justify-between bg-stone-900 p-12 text-stone-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/3bfad991-7fc9-4c85-b4b7-558ca5444229/images/055f79dad6fd5ffb9bcbe510091d1f25143d28cc700b14ce43ac17cd5fb96f42.png"
            alt="abstract"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative"><Link to="/"><Logo /></Link></div>
        <div className="relative max-w-md">
          <p className="font-display text-3xl font-medium tracking-tight leading-snug">
            Built for the way Indian teams work — async, distributed, and shipping fast.
          </p>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-stone-400">
            JOIN 2,000+ TEAMS
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Start free</h1>
          <p className="mt-2 text-sm text-muted-foreground">15 meeting hours every month, on us.</p>

          <Button
            onClick={handleGoogle}
            variant="outline"
            className="mt-8 w-full rounded-lg py-6 text-base"
            data-testid="google-signup-button"
          >
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="signup-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="signup-password-input" />
              <p className="text-xs text-muted-foreground">At least 6 characters.</p>
            </div>
            <Button type="submit" className="w-full rounded-lg py-6" disabled={loading} data-testid="signup-submit-button">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
